// src/components/PageHeader.tsx
import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import AnimatedDotBackground from '@/components/ui/AnimatedDotBackground';
import { cn } from '@/lib/utils';

interface PageHeaderProps {
  title: string;
  description?: string | ReactNode;
  className?: string;
  containerClassName?: string;
  dotBackgroundProps?: {
    intensity?: 'light' | 'medium' | 'strong';
    speed?: 'slow' | 'medium' | 'fast';
    fadeCenter?: boolean;
    enhancedCorners?: boolean;
  };
}

/**
 * A reusable page header component with animated dot background
 * for non-home pages to maintain consistent styling
 */
const PageHeader = ({
  title,
  description,
  className,
  containerClassName,
  dotBackgroundProps = {
    intensity: 'strong',
    speed: 'medium',
    fadeCenter: true,
    enhancedCorners: true,
  },
}: PageHeaderProps) => {
  return (
    <div className={cn('relative', className)}>
      {/* Animated background */}
      <AnimatedDotBackground
        intensity={dotBackgroundProps.intensity}
        speed={dotBackgroundProps.speed}
        fadeCenter={dotBackgroundProps.fadeCenter}
        enhancedCorners={dotBackgroundProps.enhancedCorners}
      />

      {/* Header content */}
      <div className={cn('pt-60 pb-32 relative', containerClassName)}>
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-5xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}>
            <h1 className="text-6xl md:text-8xl font-bold mb-6">{title}</h1>

            <motion.div
              className="h-[2px] w-48 bg-secondary mb-8"
              initial={{ width: 0 }}
              transition={{ duration: 0.7 }}
              animate={{ width: 200 }}
            />

            {description &&
              (typeof description === 'string' ? (
                <p className="text-xl text-gray-700">{description}</p>
              ) : (
                description
              ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PageHeader;
